// =============================================================================
// FILE: RunningExecutionBoard.js
// PPE → MES → Running Execution Board
//
// PURPOSE:
//   Displays all currently running executions using ExecutionCard.
//
// UI UPGRADE (2026-02-26):
//   ✔ Sticky MES header
//   ✔ Running counter badge
//   ✔ Improved loading + empty states
//   ✔ Industrial board appearance
// =============================================================================

import React, { useEffect, useState, useCallback } from "react";
import { fetchRunningExecutions } from "../services/mesExecutionService";
import ExecutionCard from "./ExecutionCard";

const RunningExecutionBoard = () => {

  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // Load running executions
  // ---------------------------------------------------------------------------
  const loadRunning = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchRunningExecutions();
      setExecutions(data || []);
    } catch (err) {
      console.error("Failed to load running executions", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Initial load + polling
  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadRunning();

    // MES live refresh
    const interval = setInterval(loadRunning, 6000000);
    return () => clearInterval(interval);
  }, [loadRunning]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div style={{ height: "100%" }}>

      {/* ================= STICKY HEADER ================= */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "#ffffff",
          paddingBottom: "10px",
          marginBottom: "10px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0 }}>
            ⚙ Running Operations
          </h3>

          <div
            style={{
              background:
                "linear-gradient(135deg,#16a34a,#22c55e)",
              color: "#fff",
              padding: "6px 14px",
              borderRadius: "999px",
              fontWeight: 600,
              fontSize: "13px",
              boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
            }}
          >
            {executions.length} ACTIVE
          </div>
        </div>
      </div>

      {/* ================= CONTENT ================= */}

      {loading && (
        <div
          style={{
            textAlign: "center",
            padding: "20px",
            color: "#6b7280",
          }}
        >
          Loading running executions...
        </div>
      )}

      {!loading && executions.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "30px",
            color: "#9ca3af",
            fontSize: "15px",
          }}
        >
          🚫 No running executions
        </div>
      )}

      {/* EXECUTION CARDS */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {executions.map((exec) => (
          <ExecutionCard
            key={exec.id}
            execution={exec}
            onRefresh={loadRunning}
          />
        ))}
      </div>

    </div>
  );
};

export default RunningExecutionBoard;